import React from 'react';
import { useAbility } from '@casl/react';
import { useTranslate } from '@bynder/localization';
import { IconClear, IconLink } from '@bynder/icons';
import { AbilityContext, Can } from '../../../abilities/Can';
import { Highlight, SearchResults } from '../../../types';
import { HelpButton } from '../Help/HelpButton';
import { RecentSearches } from '../RecentSearches';
import { isModule, isView } from '../../../helpers/windowLocation';
import { isColdfusionID } from '../../../helpers/id';
import { GroupAssets } from './GroupAssets';
import { GroupCollections } from './GroupCollections';
import { GroupStyleGuides } from './GroupStyleGuides';
import { GroupTags } from './GroupTags';
import { GroupGuidelines } from './GroupGuidelines';
import { GroupMetaproperties } from './GroupMetaproperties';
import { SEARCH_MIN_CHARS } from '../constants';
import { BannerNLS } from '../NLSSearch/BannerNLS';
import { iconSizeMedium } from '../../../helpers/iconSizes';

interface Props {
	showMainSection: boolean;
	isLoading: boolean;
	searchTerm: string;
	results: SearchResults;
	highLight: Highlight;
	onClearSearch: () => void;
	clearSearchTerm: () => void;
	defaultSearchAction: (isMPOSearch?: boolean, metapropertyName?: string) => void;
	isNLSSearchOn: boolean;
	setIsNLSSearchOn: React.Dispatch<React.SetStateAction<boolean>>;
	showNLSBanner: boolean;
	spotlightInputRef: React.RefObject<HTMLInputElement>;
	setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}

export function ResultsDropdown({
	showMainSection,
	isLoading,
	searchTerm,
	results,
	highLight,
	onClearSearch,
	clearSearchTerm,
	setSearchTerm,
	defaultSearchAction,
	isNLSSearchOn,
	setIsNLSSearchOn,
	showNLSBanner,
	spotlightInputRef,
}: Props) {
	const ability = useAbility(AbilityContext);
	const isCollection = isCollectionView();
	const canShowMedia = true; // TODO: uncomment when ability available in menu-svc. // ability.can('show', 'media_asset_bank');
	const hasAssets = results.assets.items?.length > 0 && (canShowMedia || isCollection);
	const hasTags = results.tags?.length > 0;
	const hasCollections = results.collections.items?.length > 0;
	const hasSGD = results.pages.items?.length > 0; // TODO: AND ( rc.account.hasAppAccess("SGD") AND ( rc.user.hasAppAccess("SGD") OR checkRoles("SUPERADMIN,RESELLER") ) )
	const hasGuidelines = results.guidelines.items?.length > 0;
	const hasMetaproperties = results.metaproperties && results.metaproperties.length > 0;

	const hasNoItems = !hasAssets && !hasCollections && !hasSGD && !hasGuidelines;
	const showNoResults = !isLoading && hasNoItems && searchTerm.length >= SEARCH_MIN_CHARS;
	const canShowRecentSearches =
		ability.can('show', 'spotlight_recent_searches') && searchTerm.length === 0;

	const isOpen = showMainSection || showNLSBanner;

	return (
		<div id="spotlightContainer">
			<div
				className="nav-bar-searchbox"
				data-keyword={searchTerm}
				style={{ display: isOpen ? 'inherit' : 'none' }}
			>
				<ul>
					{showNLSBanner && (
						<BannerNLS
							isNLSSearchOn={isNLSSearchOn}
							setIsNLSSearchOn={setIsNLSSearchOn}
							spotlightInputRef={spotlightInputRef}
						/>
					)}
					<div
						style={{
							display: showMainSection ? 'inherit' : 'none',
						}}
					>
						{canShowRecentSearches && <RecentSearches setSearchTerm={setSearchTerm} />}
						{hasAssets && searchTerm.length >= SEARCH_MIN_CHARS && (
							<GroupAssets
								searchTerm={searchTerm}
								results={results}
								highLight={highLight}
								defaultSearchAction={defaultSearchAction}
							/>
						)}
						{hasTags && searchTerm.length >= SEARCH_MIN_CHARS && (
							<GroupTags
								searchTerm={searchTerm}
								results={results}
								clearSearchTerm={clearSearchTerm}
							/>
						)}
						{hasMetaproperties &&
							searchTerm.length >= SEARCH_MIN_CHARS &&
							results.metaproperties.map((metaproperty) => (
								<GroupMetaproperties
									searchTerm={searchTerm}
									metaproperty={metaproperty}
									defaultSearchAction={defaultSearchAction}
									clearSearchTerm={clearSearchTerm}
									results={results}
									key={metaproperty.name}
								/>
							))}

						{!isCollection &&
							hasCollections &&
							searchTerm.length >= SEARCH_MIN_CHARS && (
								<GroupCollections searchTerm={searchTerm} results={results} />
							)}
						{hasSGD && searchTerm.length >= SEARCH_MIN_CHARS && (
							<GroupStyleGuides
								searchTerm={searchTerm}
								results={results}
								highLight={highLight}
							/>
						)}
						{hasGuidelines && searchTerm.length >= SEARCH_MIN_CHARS && (
							<GroupGuidelines searchTerm={searchTerm} results={results} />
						)}
						{showNoResults && searchTerm.length >= SEARCH_MIN_CHARS && (
							<NoResults searchTerm={searchTerm} onClearSearch={onClearSearch} />
						)}
						<Can I="show" a="spotlight_help_modal">
							<HelpButton />
						</Can>
					</div>
				</ul>
			</div>
		</div>
	);
}

interface PropsNoResults {
	searchTerm: string;
	onClearSearch: () => void;
}

function NoResults({ searchTerm, onClearSearch }: PropsNoResults) {
	const { translate } = useTranslate();
	const noSearchResultLinkSetting = null; // <cfif rc.account.hasSetting("noSearchResultLink")> - deserializeJson(rc.account.getSetting("noSearchResultLink"))
	const linkURL = ''; // noSearchResultLinkSetting.find("url", "")
	const linkLabel = ''; // #encBody(noSearchResultLinkSetting.find("label", ""))#

	return (
		<>
			<li>
				<a href="#" className="header no-click" onClick={onDisabledClick}>
					<IconClear size={iconSizeMedium} /> {translate('generalNoResultsFor')}{' '}
					<b>{searchTerm}</b>
				</a>
			</li>

			<li>
				{noSearchResultLinkSetting ? (
					<a href={linkURL} target="_blank" rel="noreferrer">
						<label>
							<IconLink size={iconSizeMedium} />
							{linkLabel}
						</label>
					</a>
				) : (
					<a href="#" className="clear-search" onClick={onClearSearch}>
						<label htmlFor="focus-new">{translate('generalAnotherSearch')}</label>
					</a>
				)}
			</li>
		</>
	);
}

const isCollectionView = () => {
	if (!isModule('collections') || !isView('view')) {
		return false;
	}

	const id = window.location.pathname.split('/')[3] || '';

	if (isColdfusionID(id)) {
		return true;
	}

	return false;
};

const onDisabledClick = (e: React.MouseEvent) => {
	e.stopPropagation();
	e.preventDefault();
};
